/**
 * platform-audit-page.tsx — Nhật ký hành động super-admin. Route `/platform/audit`.
 * Bảng: thời gian, admin, hành động, tổ chức/đối tượng, chi tiết. Lọc theo hành động.
 */
import { useState } from 'react'
import dayjs from 'dayjs'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Pagination } from '@/components/shared/pagination'
import { ErrorState } from '@/components/shared/feedback'
import { Badge } from '@/components/ui/badge'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { usePlatformAudit, usePlatformAuditActions, type PlatformAuditItem } from '@/hooks/use-platform'
import { PLATFORM_ACTION_LABELS } from './org-detail/labels'

/** Nhãn tiếng Việt cho các hành động đã biết; hành động lạ hiện nguyên chuỗi. */
const ACTION_LABEL: Record<string, string> = {
  ...PLATFORM_ACTION_LABELS,
  'org.create': 'Tạo tổ chức',
  'org.update': 'Sửa tổ chức',
  'org.license': 'Đổi cấp phép',
  'account.create': 'Tạo tài khoản',
  'account.update': 'Sửa tài khoản',
  'account.reset_password': 'Reset mật khẩu',
  enter_company: 'Vào công ty (impersonation)',
  'admin.self_update': 'Đổi thông tin cá nhân',
  'admin.password_change': 'Đổi mật khẩu super-admin',
  'billing.pricing': 'Sửa bảng giá gói',
  'billing.promo': 'Mã ưu đãi',
  release_notes: 'Thông báo cập nhật',
  landing_page: 'Trang giới thiệu',
  'manual_quote.create': 'Lập báo giá thủ công',
  'manual_quote.update': 'Sửa báo giá thủ công',
  'manual_quote.send': 'Báo giá: đánh dấu đã gửi',
  'manual_quote.cancel': 'Huỷ báo giá thủ công',
  'manual_quote.duplicate': 'Sao chép báo giá',
  'manual_quote.paid': 'Báo giá: xác nhận đã thanh toán',
  'manual_quote.template': 'Sửa mẫu báo giá',
}

/** Hành động nhạy cảm → tô đỏ để dễ soi khi rà bảo mật. */
const SENSITIVE = new Set(['enter_company', 'account.reset_password', 'org.license'])

const PAGE_SIZE = 50

export function PlatformAuditPage() {
  const [action, setAction] = useState<string>('')
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = usePlatformAudit({
    action: action || undefined,
    page,
    limit: PAGE_SIZE,
  })
  const { data: actions } = usePlatformAuditActions()

  const columns: Column<PlatformAuditItem>[] = [
    {
      key: 'createdAt', header: 'Thời gian',
      cell: (r) => (
        <span className="whitespace-nowrap text-sm text-muted-foreground">
          {dayjs(r.createdAt).format('DD/MM/YYYY HH:mm:ss')}
        </span>
      ),
    },
    {
      key: 'admin', header: 'Super-admin',
      cell: (r) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{r.admin?.fullName || '—'}</p>
          <p className="truncate text-xs text-muted-foreground">{r.admin?.email}</p>
        </div>
      ),
    },
    {
      key: 'action', header: 'Hành động',
      cell: (r) => (
        <Badge variant={SENSITIVE.has(r.action) ? 'destructive' : 'secondary'}>
          {ACTION_LABEL[r.action] ?? r.action}
        </Badge>
      ),
    },
    {
      key: 'target', header: 'Đối tượng',
      cell: (r) => (
        <div className="min-w-0 text-sm">
          {r.targetOrgName && <p className="truncate">🏢 {r.targetOrgName}</p>}
          {r.targetUserName && <p className="truncate text-muted-foreground">👤 {r.targetUserName}</p>}
          {!r.targetOrgName && !r.targetUserName && <span className="text-muted-foreground">—</span>}
        </div>
      ),
    },
    {
      key: 'meta', header: 'Chi tiết',
      cell: (r) => {
        const keys = Object.keys(r.meta ?? {})
        if (keys.length === 0) return <span className="text-muted-foreground">—</span>
        return (
          <code className="block max-w-[280px] truncate rounded bg-muted px-1.5 py-0.5 text-xs" title={JSON.stringify(r.meta)}>
            {JSON.stringify(r.meta)}
          </code>
        )
      },
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="Nhật ký hành động"
        description="Toàn bộ thao tác của super-admin: tạo/sửa tổ chức, đổi cấp phép, reset mật khẩu, vào công ty."
      />

      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={action || 'all'}
          onValueChange={(v) => { setAction(v === 'all' ? '' : v); setPage(1) }}
        >
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Lọc theo hành động" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tất cả hành động</SelectItem>
            {(actions ?? []).map((a) => (
              <SelectItem key={a} value={a}>{ACTION_LABEL[a] ?? a}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {data && <span className="text-sm text-muted-foreground">{data.total} bản ghi</span>}
      </div>

      {isError ? (
        <ErrorState />
      ) : (
        <DataTable
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(r) => r.id}
          loading={isLoading}
          emptyTitle="Chưa có bản ghi nào"
        />
      )}

      {data && data.total > PAGE_SIZE && (
        <Pagination
          page={page}
          limit={PAGE_SIZE}
          total={data.total}
          onPageChange={setPage}
        />
      )}
    </div>
  )
}
